import { useQuery } from "@tanstack/react-query"

import {
  getAgentTools,
  getAskOSChatHistory,
  getKosTools,
  getUserPrompts,
  searchAskOSChatHistory
} from "~/api/os.agents"
import useGlobalStore from "~/store/global"
import { ITokenInfo } from "~/types/global"

export function useGetAskOSChatHistory(templateFill = false) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["askos-chat-history", templateFill],
    queryFn: async () => {
      const res = await getAskOSChatHistory(tokenInfo as ITokenInfo, {
        templateFill
      })
      return res.data
    },
    enabled: !!tokenInfo,
    // The global QueryClient defaults to refetchOnMount:false + 2-min
    // staleTime, so the history list would NOT refetch when the drawer
    // (or history panel) remounts — leaving it stale / appearing "not
    // called" on open. Chat history must be current each time it's shown.
    staleTime: 0,
    refetchOnMount: "always"
  })
}

// Content search over Ask OS chat sessions (user query + LLM response).
// `query` should already be trimmed/debounced by the caller; the hook only
// runs when it's non-empty so an empty box never fires a request.
export function useSearchAskOSChatHistory(query: string) {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  const trimmed = query.trim()
  return useQuery({
    queryKey: ["askos-chat-search", trimmed],
    queryFn: async () => {
      const res = await searchAskOSChatHistory(tokenInfo as ITokenInfo, trimmed)
      return res.data
    },
    enabled: !!tokenInfo && trimmed.length > 0,
    staleTime: 30_000
  })
}

export const useGetAgentTools = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["tenant", "user", "copilot", "agentTools", tokenInfo?.token],
    queryFn: async () => {
      const res = await getAgentTools(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo?.token
  })
}

export const useGetKosTools = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["tenant", "user", "copilot", "kosTools", tokenInfo?.token],
    queryFn: async () => {
      const res = await getKosTools(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo?.token
  })
}

export const useGetUserPrompts = () => {
  const tokenInfo = useGlobalStore((store) => store.tokenInfo)
  return useQuery({
    queryKey: ["tenant", "user", "copilot", "prompts", tokenInfo?.token],
    queryFn: async () => {
      const res = await getUserPrompts(tokenInfo as ITokenInfo)
      return res.data
    },
    enabled: !!tokenInfo?.token
  })
}
